import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { FaClipboardList } from '@react-icons/all-files/fa/FaClipboardList';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../commons/Breadcrumb';
import PageHeader from '../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../commons/PageAlert';
import ProfaseDataGrid from '../../commons/Datagrid';
import LinkCell from '../../commons/LinkCell';
import UserInfo from '../../commons/UserInfo';
import { useAppDispatch, useAppSelector } from '../../store';
import { getEntities } from '../../manager/workflow-instancia-estado-nodos.reducer';

const NODO_ESTADO_OPTS = [
  { value: 'PENDIENTE', label: 'Pendiente' },
  { value: 'GENERADO', label: 'Generado' },
  { value: 'EJECUCION', label: 'En ejecución' },
  { value: 'FINALIZADO', label: 'Finalizado' },
  { value: 'VENCIDO', label: 'Vencido' },
  { value: 'CANCELADO', label: 'Cancelado' },
  { value: 'BLOQUEADO', label: 'Bloqueado' },
];

const WF_ESTADO_OPTS = [
  { value: 'GENERADO', label: 'Generado' },
  { value: 'EJECUCION', label: 'En ejecución' },
  { value: 'FINALIZADO', label: 'Finalizado' },
];

/** Build a usuario-shaped object for `UserInfo` from flat DTO fields. */
function usuarioFromFlat(id?: number | null, nombreCompleto?: string | null) {
  if (id == null) return null;
  return { id, nombre: nombreCompleto ?? '', apellido: '' };
}

function NodoEstadoCell({ estado, color }: { estado?: string; color?: string }) {
  if (!estado) return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
  const border = color ? `2px solid ${color}` : '1px solid red';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        border,
        whiteSpace: 'nowrap',
        background: color,
        color: 'white'
      }}
    >
      {estado}
    </span>
  );
}

/** Default URL when opening /actividades with no query string. */
function buildDefaultActividadesSearchParams(): URLSearchParams {
  const p = new URLSearchParams();
  p.set('page', '1');
  p.set('sort', 'nodoEstado,asc');
  p.append('nodoEstado', 'VENCIDO');
  p.append('nodoEstado', 'PENDIENTE');
  p.append('nodoEstado', 'BLOQUEADO');
  return p;
}

export default function ActividadesPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  // Apply defaults only for a completely empty query
  useLayoutEffect(() => {
    const q = location.search.replace(/^\?/, '').trim();
    if (q !== '') return;
    setSearchParams(buildDefaultActividadesSearchParams(), { replace: true });
  }, [location.pathname, location.search, setSearchParams]);

  const entities = useAppSelector(s => (s as any).workflowInstanciaEstadoNodos.entities);
  const totalItems = useAppSelector(s => (s as any).workflowInstanciaEstadoNodos.totalItems);
  const loading = useAppSelector(s => (s as any).workflowInstanciaEstadoNodos.loading);
  const errorMessage = useAppSelector(s => (s as any).workflowInstanciaEstadoNodos.errorMessage);

  const [rows, setRows] = useState<any[]>([]);
  const { alert, showToast } = usePageAlert();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) {
      showToast('success', msg);
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setRows(entities ?? []);
  }, [entities]);

  const fetchData = (params: any) => dispatch(getEntities(params));

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.5 },
    {
      field: 'workflowInstanciaId',
      headerName: 'Instancia',
      flex: 0.8,
      renderCell: (p: any) => p.row.workflowInstanciaId ?? '—',
    },
    {
      field: 'workflowNombre',
      headerName: 'Workflow',
      flex: 2,
      renderCell: (p: any) => (
        <LinkCell
          label={p.row.workflowNombre ?? '—'}
          filterName="workflowId"
          filterValue={p.row.workflowId}
        />
      ),
    },
    {
      field: 'categoriaActividadDescripcion',
      headerName: 'Actividad',
      flex: 2,
      renderCell: (p: any) => (
        <LinkCell
          label={p.row.categoriaActividadDescripcion ?? null}
          filterName="categoriaActividadId"
          filterValue={p.row.categoriaActividadId}
        />
      ),
    },
    {
      field: 'empresaRfc',
      headerName: 'RFC',
      flex: 1.2,
      renderCell: (p: any) => (
        <LinkCell
          label={p.row.empresaRfc ?? null}
          filterName="empresaId"
          filterValue={p.row.empresaId}
        />
      ),
    },
    {
      field: 'empresaRazonSocial',
      headerName: 'Razón social',
      flex: 2.5,
      renderCell: (p: any) => (
        <LinkCell
          label={p.row.empresaRazonSocial ?? null}
          filterName="empresaId"
          filterValue={p.row.empresaId}
        />
      ),
    },
    {
      field: 'grupoAlias',
      headerName: 'Grupo',
      flex: 1.8,
      renderCell: (p: any) => (
        <LinkCell
          label={p.row.grupoAlias ?? null}
          filterName="grupoId"
          filterValue={p.row.grupoId}
        />
      ),
    },
    {
      field: 'usuarioId',
      headerName: 'Usuario',
      flex: 1.5,
      renderCell: (p: any) => (
        <UserInfo
          usuario={usuarioFromFlat(p.row.usuarioId, p.row.usuarioNombreCompleto)}
          onClick={u => {
            const next = new URLSearchParams(searchParams);
            next.set('usuarioId', String(u.id));
            next.set('page', '1');
            setSearchParams(next);
          }}
        />
      ),
    },
    {
      field: 'autorizaId',
      headerName: 'Autoriza',
      flex: 1.5,
      renderCell: (p: any) => (
        <UserInfo
          usuario={usuarioFromFlat(p.row.autorizaId, p.row.autorizaNombreCompleto)}
          onClick={u => {
            const next = new URLSearchParams(searchParams);
            next.set('autorizaId', String(u.id));
            next.set('page', '1');
            setSearchParams(next);
          }}
        />
      ),
    },
    {
      field: 'nodoEstado',
      headerName: 'Estado nodo',
      flex: 1.2,
      renderCell: (p: any) => (
        <NodoEstadoCell estado={p.row.nodoEstado} color={p.row.color ?? '#ff0000'} />
      ),
    },
    {
      field: 'workflowEstado',
      headerName: 'Estado WF',
      flex: 1,
    },
  ];

  /** Filter `name` must match backend query parameter names (flat DTO fields). */
  const filters = [
    { label: 'Workflow', name: 'workflowNombre' },
    { label: 'Actividad', name: 'categoriaActividadDescripcion' },
    { label: 'Actividad ID', name: 'categoriaActividadId' },
    { label: 'RFC', name: 'empresaRfc' },
    { label: 'Razón social', name: 'empresaRazonSocial' },
    { label: 'Grupo', name: 'grupoAlias' },
    { label: 'Grupo ID', name: 'grupoId' },
    { label: 'Usuario', name: 'usuarioNombreCompleto' },
    { label: 'Usuario ID', name: 'usuarioId' },
    { label: 'Autoriza', name: 'autorizaNombreCompleto' },
    { label: 'Autoriza ID', name: 'autorizaId' },
    { label: 'Instancia WF', name: 'workflowInstanciaId' },
    { label: 'Workflow ID', name: 'workflowId' },
    { label: 'Empresa ID', name: 'empresaId' },
    {
      label: 'Estado nodo',
      name: 'nodoEstado',
      type: 'select' as const,
      options: NODO_ESTADO_OPTS,
    },
    {
      label: 'Estado WF',
      name: 'workflowEstado',
      type: 'select' as const,
      options: WF_ESTADO_OPTS,
    },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb
        items={[
          { label: 'Inicio', to: '/', icon: <FaHome /> },
          { label: 'Actividades', icon: <FaClipboardList /> },
        ]}
      />

      <PageHeader
        icon={<FaClipboardList />}
        iconColor="#3b82f6"
        title="Actividades"
        subtitle="Estado de nodos por instancia de workflow"
      />

      {alert && <PageAlert alert={alert} />}
      {errorMessage && (
        <div style={g.errorBanner} role="alert">
          {errorMessage}
        </div>
      )}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24, padding: '0 4px' },
  errorBanner: {
    padding: '10px 14px',
    background: '#fef2f2',
    color: '#b91c1c',
    border: '1px solid #fecaca',
    borderRadius: 8,
    fontSize: 13,
  },
};
